import { subMonths, subWeeks, subYears } from "date-fns";

/**
 * Selectable timeframes for the "Show Shipments From Last" filter. The value is
 * a stable key persisted to storage; the label is what the user sees.
 */
export type ShipmentDateRangeValue = "1w" | "1m" | "3m" | "6m" | "1y" | "2y";

export type ShipmentDateRangeOption = {
	label: string;
	value: ShipmentDateRangeValue;
};

export const shipmentDateRangeOptions: readonly ShipmentDateRangeOption[] = [
	{ label: "1 Week", value: "1w" },
	{ label: "1 Month", value: "1m" },
	{ label: "3 Months", value: "3m" },
	{ label: "6 Months", value: "6m" },
	{ label: "1 Year", value: "1y" },
	{ label: "2 Years", value: "2y" },
];

export const defaultShipmentDateRange: ShipmentDateRangeValue = "1m";

const shipmentDateRangeValues = new Set<string>(
	shipmentDateRangeOptions.map((option) => option.value),
);

/**
 * Type guard for a persisted/serialized timeframe value. Storage can hold a
 * stale or hand-edited string after the option list changes, so anything not
 * in the current option set is treated as invalid.
 */
export const isShipmentDateRangeValue = (
	value: unknown,
): value is ShipmentDateRangeValue =>
	typeof value === "string" && shipmentDateRangeValues.has(value);

/**
 * Coerces an unknown (e.g. persisted) value to a valid timeframe, falling back
 * to the default when it is stale or invalid. This keeps the dropdown display
 * and the grid filter in agreement — without it an invalid stored value shows
 * as the default in the dropdown while the filter computes an invalid cutoff
 * and hides every shipment.
 */
export const coerceShipmentDateRange = (
	value: unknown,
): ShipmentDateRangeValue =>
	isShipmentDateRangeValue(value) ? value : defaultShipmentDateRange;

/**
 * Returns the earliest pickup date a shipment may have to remain visible for
 * the given timeframe. Shipments whose pickup date is on or after this cutoff
 * (including future-dated shipments) are kept.
 */
export const getShipmentDateRangeCutoff = (
	value: ShipmentDateRangeValue,
	now: Date = new Date(),
): Date => {
	switch (value) {
		case "1w": {
			return subWeeks(now, 1);
		}
		case "1m": {
			return subMonths(now, 1);
		}
		case "3m": {
			return subMonths(now, 3);
		}
		case "6m": {
			return subMonths(now, 6);
		}
		case "1y": {
			return subYears(now, 1);
		}
		case "2y": {
			return subYears(now, 2);
		}
		default: {
			// Defensive fallback: an out-of-range value must never produce an
			// invalid cutoff (which would filter out every load).
			return getShipmentDateRangeCutoff(defaultShipmentDateRange, now);
		}
	}
};
